import { Box, Typography, IconButton } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { motion } from "framer-motion";

const MotionBox = motion.create ? motion.create(Box) : motion(Box);

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: 0.2 },
  },
};

const DeleteButton = ({ onClick }) => (
  <IconButton
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      position: "absolute",
      top: 8,
      right: 8,
      width: 28,
      height: 28,
      bgcolor: "rgba(255,255,255,0.95)",
      color: "#EF4444",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      transition: "all 0.2s ease",
      "&:hover": {
        bgcolor: "#FEE2E2",
        transform: "scale(1.1)",
      },
    }}
  >
    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
  </IconButton>
);

const ViewButton = ({ onClick, theme }) => (
  <Box
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="view-button"
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      px: 1.5,
      py: 0.5,
      bgcolor: "rgba(255,255,255,0.95)",
      color: "#64748B",
      borderRadius: 1.5,
      fontSize: 10,
      fontWeight: 600,
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      opacity: 0,
      cursor: "pointer",
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
      "&:hover": {
        bgcolor: "#FFFFFF",
        transform: "translate(-50%, -50%) scale(1.05)",
        color: theme?.palette?.primary?.main || "#7C3AED",
      },
    }}
  >
    View Image
  </Box>
);

export const ImageCard = ({ id, file, url, remote, onDelete, onView, theme }) => (
  <MotionBox
    key={id}
    variants={itemVariants}
    layout
    sx={{
      position: "relative",
      borderRadius: 3,
      overflow: "hidden",
      aspectRatio: "1",
      "&:hover": {
        "& .image-overlay": {
          opacity: 1,
        },
        "& .view-button": {
          opacity: 1,
        },
        "& img": {
          transform: "scale(1.05)",
        },
      },
    }}
  >
    <Box
      component="img"
      src={url || "/assets/placeholder.png"}
      alt={file.name}
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transition: "transform 0.3s ease",
      }}
    />
    <Box
      className="image-overlay"
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "rgba(0,0,0,0.3)",
        opacity: 0,
        transition: "opacity 0.3s ease",
      }}
    />
    <ViewButton onClick={onView} theme={theme} />
    <DeleteButton onClick={() => onDelete({ id, remote })} />
  </MotionBox>
);

export const VideoCard = ({ id, file, url, remote, onDelete, onPreview }) => (
  <MotionBox
    key={id}
    variants={itemVariants}
    layout
    onClick={onPreview}
    sx={{
      position: "relative",
      borderRadius: 3,
      overflow: "hidden",
      cursor: "pointer",
      aspectRatio: "16/10",
      "&:hover": {
        "& .play-button": {
          transform: "translate(-50%, -50%) scale(1.1)",
          bgcolor: "rgba(255,255,255,1)",
        },
        "& .video-thumbnail": {
          transform: "scale(1.05)",
        },
      },
    }}
  >
    {file.type.includes("video") ? (
      <Box
        component="video"
        src={url}
        className="video-thumbnail"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.3s ease",
        }}
      />
    ) : (
      <Box
        component="img"
        src="/assets/placeholder.png"
        className="video-thumbnail"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.3s ease",
        }}
      />
    )}
    <Box
      className="play-button"
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 48,
        height: 48,
        bgcolor: "rgba(255,255,255,0.9)",
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        transition: "all 0.3s ease",
      }}
    >
      <PlayArrowIcon sx={{ fontSize: 28, color: "#666" }} />
    </Box>
    <DeleteButton onClick={() => onDelete({ id, remote })} />
  </MotionBox>
);

const truncateFileName = (name, max = 18) => {
  const dot = name.lastIndexOf(".");
  if (dot === -1) {
    return name.length > max ? name.slice(0, max) + "…" : name;
  }
  const ext = name.slice(dot);
  const base = name.slice(0, max - ext.length - 1);
  return base.length > 0 ? `${base}…${ext}` : name;
};

const formatFileSize = (size) => {
  const mb = size / 1024 / 1024;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(size / 1024).toFixed(0)} KB`;
};

export const DocumentCard = ({ id, file, url, remote, onDelete, onPreview }) => (
  <MotionBox
    key={id}
    variants={itemVariants}
    layout
    onClick={onPreview}
    sx={{
      position: "relative",
      bgcolor: "#FAFBFC",
      borderRadius: 3,
      p: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      cursor: "pointer",
      border: "1px solid #F0F0F0",
      transition: "all 0.2s ease",
      "&:hover": {
        bgcolor: "#F5F6F8",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        bgcolor: "#FEE2E2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <PictureAsPdfIcon sx={{ color: "#EF4444", fontSize: 24 }} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: "#1F2937",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {truncateFileName(file.name, 20)}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: "#9CA3AF",
          mt: 0.25,
        }}
      >
        {formatFileSize(file.size)}
      </Typography>
    </Box>
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onDelete({ id, remote });
      }}
      sx={{
        width: 28,
        height: 28,
        color: "#EF4444",
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: "#FEE2E2",
          transform: "scale(1.1)",
        },
      }}
    >
      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </MotionBox>
);
